import { DataObject } from './report.model';
import { TemplateField, TemplateFieldsCatalog } from './template.model';
import {
  PPTGroupsDataTypes,
  WindwardDataType,
  WindwardResolveBy,
  WindwardSlideSubType,
  WindwardSlideType,
} from './windward-blueprint.model';

/**
 * Enum that contains the blueprint layouts. The values refer to attributes that
 * are part of the {@link BlueprintWithLayouts}.
 *
 * Sometimes, it is also mapped inside of {@link ReportRecord} in order to
 * interpolate the record in the specified layout.
 *
 * @see BlueprintWithLayouts
 * @see ReportRecord
 */
export enum Layout {
  MainLayout = 'mainLayout',
  Contacts = 'contacts',
  AdditionalLayout = 'additionalLayout',
  FloorPlan = 'floorplan',
  RecordsAddressLayout = 'recordsAddressLayout',
  ThanksPage = 'thanksPage',
  Cover = 'cover',
  AvailabilityFloorplan = 'availabilityFloorplan',
  StackingPlanTable = 'stackingPlanTable',
  Windward = 'windward',
}

/**
 * Interface that refers to the data structure that we receive from a
 * hard-coded JSON file that is currently stored in the back-end.
 *
 * Each attribute refers to {@link Layout} enum
 *
 * @see Layout
 */
export type BlueprintWithLayouts = {
  [layoutKey in Layout]?: { [columnId: number]: ColumnBase };
};

/**
 * @deprecated
 *
 * Interface that contains the original blueprint layout. This was meant to be
 * the full interface for the object we receive from a hard-coded JSON file that
 * is currently stored in the back-end.
 *
 * @see BlueprintWithLayouts to be used instead.
 */
interface BlueprintWithNoLayoutBase {
  /** @deprecated in order to use mainLayout, we are deprecating this field */
  [columnId: number]: ColumnBase;
  coverPath?: string;
  coverSlideInfo?: CoverPageBlueprint;
}

/**
 * This type represents the union between the old (deprecated) blueprint
 * interface and the new structure we use to configure our blueprint files.
 *
 * @see BlueprintWithNoLayoutBase
 * @see BlueprintWithLayouts
 *
 * TODO: we need to migrate all of our blueprints to use the
 *  {@link BlueprintWithLayouts} type. After the migration is done, we need
 *  to rename the type to BlueprintBase.
 */
export type BlueprintBase = BlueprintWithNoLayoutBase & BlueprintWithLayouts;

export interface ColumnBase {
  [groupName: string]: GroupBase;
}

export interface GroupBase<
  T extends { id: number; [otherAttrs: string]: any } = { id: number; [otherAttrs: string]: any },
> {
  order?: number;
  /**
   * This attribute has multiple uses. It is being used by the the
   * template-builder.service (in miq-frontend project) to generate different
   * types of PPT elements through PptxGenJS. The values for this usage are
   * listed in the {@link PPTGroupsDataTypes} enum.
   *
   * This attribute is also being used by the reporting server to generate data
   * structures that are part of the data source being used to generate
   * Windward templates. The values for this usage are listed in the
   * {@link WindwardDataType} enum.
   */
  dataType: string | PPTGroupsDataTypes | WindwardDataType;
  header?: { label: string; options?: { [opt: string]: any } };
  fields: Array<T>;
  htmlHeader?: boolean;
  htmlFooter?: boolean;
  filterOutInHtml?: boolean;
  filterOutInPPT?: boolean;
  options?: { [name: string]: any };
  tableConfigs?: { [option: string]: any };
  contacts?: boolean;
  coverPath?: string;
  hasImages?: boolean;
  isRecordsForSummaryMap?: boolean;
  isSummaryMapImage?: boolean;
  specialCase?: boolean;
  availabilityDetailsBlue?: boolean;
  /**
   * type of structure required for the group in the data source.
   *
   * @see WindwardDataType
   */
  slideType?: WindwardSlideType;
  /**
   * used **only** in combination with "per-record" slideType. It is used
   * to add an extra level of slides, commonly used for property grouped records,
   * like availabilities, lease records, etc.
   *
   * It's **important** to note the if this is in use, the data type needs to be
   * one of the data types prefixed with "multiple-"
   */
  slideSubType?: WindwardSlideSubType;
  /**
   * This optional attribute is used mainly by blueprint-based windward downloads.
   * Also, this is currently only used by the resolved-field list and
   * multiple-resolved-field-list data types. It allow them to filter out any
   * field that doesn't contain values. It could be a hyphen, an empty string or
   * a null value.
   */
  filterEmpty?: boolean;
  /**
   * string used by the reporting server to assign a custom name to the root
   * data source attribute. This attribute contains all the processed groups
   * that belong to the slide.
   *
   * For an example see {@link GroupBase.attributeName}
   */
  parentSlide?: string;
  /**
   * string used by the reporting server to assign a custom name to the slide
   * attribute.
   *
   * @example
   *
   * // the blueprint group:
   * {
   *  "slide1Header": {
   *     "slideType": "general",
   *     "parentSlide": "summarySlide",
   *     "dataType": "resolved-field",
   *     "attributeName": "myCustomAttribute", // this is the attributeName
   *     "fields": [
   *       { "id": 49 },
   *       { "id": 349, "displayFnArgumentsOrder": [854, 857] }
   *     ]
   *   }
   * }
   *
   * // the datasource result:
   * {
   *   summarySlide: {
   *     myCustomAttribute: { label: '', value: '' }
   *   }
   * }
   */
  attributeName?: string;
  /**
   * this attribute is used by the reporting server, specially when the
   * {@link GroupBase.slideType} is {@link WindwardSlideType.general}, to
   * know how to resolve slide attributes with the same name.\
   *
   *
   * @example
   * i.e. we need a single slide which contains multiple records in a list.
   * this slide needs a header and a list of records in the datasource.
   * due to the structure nature, each record will contain the same exact
   * group for the header. As we should have a single header for this slide,
   * independently from the number of records, we should use the 'replace'
   * value.
   *
   * In the case of the record list, we need to 'push' every resolved value
   * to an array, so we can render a list of records in the slide
   */
  resolveBy?: WindwardResolveBy;
}

/**
 * interpolation data structure obtained after processing the blueprint, the
 * entity catalog, and the template fields for a report template.
 *
 * @see BlueprintBase
 * @example
 *
 * [ // first array represents what we define as a column in the blueprint
 *   [ // array of groups that belong to a column
 *     {
 *       "order": 1,
 *       "dataType": "text-box",
 *       "header": null,
 *       "htmlHeader": true,
 *       "filterOutInPPT": false,
 *       "fields": [
 *         {
 *           "id": 49,
 *           "label": "Property Name",
 *           "value": "a value",
 *           "options": {
 *             "headerSortOrder": 1
 *           }
 *         },
 *         {
 *           "id": 27,
 *           "label": "Address",
 *           "value": "a value",
 *           "options": {
 *             "headerSortOrder": 2,
 *             "hasAnchor": true
 *           }
 *         }
 *       ]
 *     }
 *   ]
 * ]
 *
 */
export type InterpolatedBlueprint<T extends { id: number } = TemplateField> = Array<Array<GroupBase<T>>>;

/** @deprecated */
export interface CoverPageBlueprint {
  cover: { coverImagePath: string; coverBackground: string };
  reportName: { options: DataObject };
  dateOfReport: { options: DataObject };
  contacts: { options: DataObject; contactConfig: [] };
}

export type CoverBlueprint = BlueprintBase;

export interface FullTemplateInfo {
  coverSlideInfo: CoverBlueprint;
  blueprint: BlueprintBase;
  templateCatalog: TemplateFieldsCatalog;
}
